import {
  EscPosPrinterParams,
  MessageRenderParams,
  MessageRenderType,
} from '@pedix-workspace/web-printer';
import {
  currencyFormat,
  getCurrencyLocaleDataByCountryCode,
  TaxVoucherOptions,
} from '@pedix-workspace/utils';
import {
  OutputRendererEscPos,
  OrderMessageRendererBase,
  OutputRendererWhatsapp,
  OutputRendererHtml,
} from '@pedix-workspace/web-printer';
import { ArcaLineItemsByOrder, ArcaVoucherLetter, TaxVoucherArca } from './arca.types';
import { format } from 'date-fns';
import {
  getArcaAliquotValue,
  getArcaDocumentTypeName,
  getArcaVatConditionName,
  getDocumentName,
  getVoucherLegend,
  getDocumentTypeCode,
  getVoucherLetter,
} from './arca.utils';
import {
  ARCA_CURRENCY_FORMAT_OPTIONS,
  determineRenderingStrategy,
  getArcaVoucherQrUrl,
  processLineItemsByOrder,
} from './arca-voucher.utils';
import { ArcaAliquotType } from './arca.runtime-types';
import QRCode from 'qrcode';

export async function generateArcaVoucherTicket({
  rendererType,
  renderParams,
  printerParams,
  taxVoucher,
}: {
  taxVoucher: TaxVoucherArca;
  rendererType: MessageRenderType;
  renderParams: MessageRenderParams;
  printerParams?: EscPosPrinterParams;
}) {
  const voucherLetter = getVoucherLetter(taxVoucher.details.type);
  const taxVoucherOptions: TaxVoucherOptions = {
    groupingConceptName: '',
    groupByOrder: false,
    ...taxVoucher.details.taxVoucherOptions,
  };

  // Process orders according to strategy
  const processedLineItems = processLineItemsByOrder({
    lineItemsByOrder: taxVoucher.details.lineItemsByOrder,
    taxVoucherOptions,
    voucherLetter,
  });

  let renderer: OrderMessageRendererBase;

  switch (rendererType) {
    case 'whatsapp':
      renderer = new OutputRendererWhatsapp(renderParams);
      break;
    case 'html':
      renderer = new OutputRendererHtml(renderParams);
      break;
    case 'escpos':
      renderer = new OutputRendererEscPos(renderParams, printerParams);
      break;
  }

  renderEstablishmentData(renderer, { taxVoucher }, renderParams);
  renderSeparator(renderer);
  renderVoucherData(renderer, { taxVoucher });
  renderSeparator(renderer);
  renderBuyerData(renderer, { taxVoucher });
  renderSeparator(renderer);

  renderOrderItems(
    renderer,
    {
      processedLineItems,
      voucherLetter,
      taxVoucherOptions,
    },
    renderParams,
  );
  renderSeparator(renderer);

  renderOrderTotals(renderer, { taxVoucher, voucherLetter }, renderParams);

  renderSeparator(renderer);

  await renderOrderFooter(renderer, { taxVoucher }, renderParams);

  return renderer.output();
}

type NodeRenderer<T = unknown, R = MessageRenderParams> = {
  (renderer: OrderMessageRendererBase, options?: T, renderOptions?: R): void;
};

const renderEmptyLine: NodeRenderer = renderer => renderer.renderEmptyLine();
const renderSeparator: NodeRenderer = renderer => renderer.renderSeparator();

const renderEstablishmentData: NodeRenderer<{ taxVoucher: TaxVoucherArca }> = (
  renderer,
  { taxVoucher },
) => {
  renderer.renderLine(taxVoucher.details.accountDetails.companyName, {
    alignment: 'center',
    bold: true,
  });
  renderer.renderLine(taxVoucher.details.accountDetails.address, { alignment: 'center' });

  renderer.renderEmptyLine();

  renderer.renderKeyValueLine({
    keyText: 'CUIT',
    valueText: taxVoucher.details.accountDetails.cuit + '',
  });
  renderer.renderLine(getArcaVatConditionName(taxVoucher.details.accountDetails.vatCondition));

  if (taxVoucher.details.accountDetails.operationsStartDate) {
    renderer.renderKeyValueLine({
      keyText: 'Inicio actividades',
      valueText: format(taxVoucher.details.accountDetails.operationsStartDate, 'dd/MM/yyyy'),
    });
  }
  if (taxVoucher.details.accountDetails.grossIncome) {
    renderer.renderKeyValueLine({
      keyText: 'Ingresos brutos',
      valueText: taxVoucher.details.accountDetails.grossIncome,
    });
  }
};

const renderVoucherData: NodeRenderer<{
  taxVoucher: TaxVoucherArca;
}> = (renderer, { taxVoucher }) => {
  const documentName = getDocumentName(taxVoucher.details.type);
  const voucherLetter = getVoucherLetter(taxVoucher.details.type);
  const documentTypeCode = getDocumentTypeCode(taxVoucher.details.type);
  const legend = getVoucherLegend(taxVoucher);

  renderer.renderText(`${documentName} ${voucherLetter}`, {
    bold: true,
    uppercase: true,
  });
  renderer.renderText(` (Cod. ${documentTypeCode})`);
  renderer.renderLineBreak();
  if (legend) {
    renderer.renderText(legend, { bold: true, uppercase: true });
    renderer.renderLineBreak();
  }
  renderer.renderLine(`Nro.: ${taxVoucher.details.voucherRef}`);
  renderer.renderLine(`Fecha: ${format(taxVoucher.details.created, 'dd/MM/yyyy')}`);
};

const renderBuyerData: NodeRenderer<{
  taxVoucher: TaxVoucherArca;
}> = (renderer, { taxVoucher }) => {
  if (taxVoucher.details.buyerBillingInfo.name) {
    renderer.renderKeyValueLine({
      keyText: 'Razón social / Nombre',
      valueText: taxVoucher.details.buyerBillingInfo.name,
    });
  }
  if (taxVoucher.details.buyerBillingInfo.documentNumber) {
    renderer.renderKeyValueLine({
      keyText: getArcaDocumentTypeName(taxVoucher.details.buyerBillingInfo.documentType),
      valueText: taxVoucher.details.buyerBillingInfo.documentNumber + '',
    });
  }
  if (taxVoucher.details.buyerBillingInfo.address) {
    renderer.renderKeyValueLine({
      keyText: 'Domicilio comercial',
      valueText: taxVoucher.details.buyerBillingInfo.address,
    });
  }
  renderer.renderKeyValueLine({
    keyText: 'Cond. IVA',
    valueText: getArcaVatConditionName(taxVoucher.details.buyerBillingInfo.vatCondition),
  });
};

const renderOrderItems: NodeRenderer<{
  processedLineItems: ArcaLineItemsByOrder[];
  voucherLetter: ArcaVoucherLetter;
  taxVoucherOptions: TaxVoucherOptions;
}> = (renderer, { processedLineItems, voucherLetter, taxVoucherOptions }, renderOptions) => {
  const renderingStrategy = determineRenderingStrategy(processedLineItems, taxVoucherOptions);

  processedLineItems.forEach((order, orderIndex) => {
    // Add order header for multiple orders without grouping
    if (renderingStrategy === 'multiple-orders-with-headers' && processedLineItems.length > 1) {
      const colSpan = voucherLetter === 'A' ? 6 : 4;
      renderer.renderLine(`Pedido #${order.orderId}`, { bold: true, uppercase: true });
    }

    // renderer.renderLine('Descripción', { bold: true, uppercase: true });

    // if (voucherLetter === 'A') {
    //   renderer.renderColumns(['Cant./P. Unit.', 'IVA%', 'Importe c/IVA'], [4, 1, 3], {
    //     monospace: true,
    //   });
    // } else {
    //   renderer.renderColumns(['Cant./P. Unit.', 'Importe'], [4, 3], { monospace: true });
    // }
    // renderer.renderEmptyLine();

    // Add product rows
    order.lineItems.forEach(product => {
      const formattedUnitPrice = `${currencyFormat(
        product.unitPrice,
        getCurrencyLocaleDataByCountryCode(renderOptions.currencyCode),
        {
          ...ARCA_CURRENCY_FORMAT_OPTIONS,
          skipSymbol: true,
        },
      )}`;
      const formattedTotalPrice = `${currencyFormat(product.totalAmount, getCurrencyLocaleDataByCountryCode(renderOptions.currencyCode), ARCA_CURRENCY_FORMAT_OPTIONS)}`;

      renderer.renderLine('- ' + product.name);

      if (voucherLetter === 'A') {
        const aliquotLabel = product.aliquot === 'exempt' ? 'Exento' : `${product.aliquot}%`;

        renderer.renderColumns(
          [`${product.quantity}x${formattedUnitPrice}`, aliquotLabel, formattedTotalPrice],
          [5, 2, 4],
          { monospace: true },
        );
      } else {
        renderer.renderColumns(
          [
            `${renderer.getIdentationByLevel(1)}${product.quantity}x${formattedUnitPrice}`,
            formattedTotalPrice,
          ],
          [4, 3],
          { monospace: true },
        );
      }
    });
  });
};

const renderOrderTotals: NodeRenderer<{
  taxVoucher: TaxVoucherArca;
  voucherLetter: ArcaVoucherLetter;
}> = (renderer, { taxVoucher, voucherLetter }, { translations, currencyCode }) => {
  if (voucherLetter === 'A') {
    const ivaRows: { label: string; amount: number }[] = [];

    taxVoucher.details.voucherTotals.vatDetails?.forEach(vatDetail => {
      ivaRows.push({
        label: `IVA ${vatDetail.aliquot}%:`,
        amount: vatDetail.vatAmount,
      });
    });
    renderer.renderColumns(
      [
        `Imp. Neto Gravado:`,
        currencyFormat(
          taxVoucher.details.voucherTotals.netAmount,
          getCurrencyLocaleDataByCountryCode(currencyCode),
          ARCA_CURRENCY_FORMAT_OPTIONS,
        ),
      ],
      [4, 3],
      { monospace: true },
    );
    if (taxVoucher.details.voucherTotals.exemptAmount > 0) {
      renderer.renderColumns(
        [
          `IVA Exento:`,
          currencyFormat(
            taxVoucher.details.voucherTotals.exemptAmount,
            getCurrencyLocaleDataByCountryCode(currencyCode),
            ARCA_CURRENCY_FORMAT_OPTIONS,
          ),
        ],
        [4, 3],
        { monospace: true },
      );
    }
    ivaRows.forEach(row => {
      renderer.renderColumns(
        [
          row.label,
          currencyFormat(
            row.amount,
            getCurrencyLocaleDataByCountryCode(currencyCode),
            ARCA_CURRENCY_FORMAT_OPTIONS,
          ),
        ],
        [4, 3],
        { monospace: true },
      );
    });
    renderer.renderColumns(
      [
        `Imp. Total:`,
        currencyFormat(
          taxVoucher.details.voucherTotals.totalAmount,
          getCurrencyLocaleDataByCountryCode(currencyCode),
          ARCA_CURRENCY_FORMAT_OPTIONS,
        ),
      ],
      [4, 3],
      { monospace: true, bold: true, uppercase: true },
    );
  } else {
    const subtotalAmount =
      taxVoucher.details.voucherTotals.totalAmount -
      taxVoucher.details.voucherTotals.tributesAmount;
    renderer.renderColumns(
      [
        `Subtotal:`,
        currencyFormat(
          subtotalAmount,
          getCurrencyLocaleDataByCountryCode(currencyCode),
          ARCA_CURRENCY_FORMAT_OPTIONS,
        ),
      ],
      [4, 3],
      { monospace: true },
    );
    renderer.renderColumns(
      [
        `Importe otros tributos:`,
        currencyFormat(
          taxVoucher.details.voucherTotals.tributesAmount,
          getCurrencyLocaleDataByCountryCode(currencyCode),
          ARCA_CURRENCY_FORMAT_OPTIONS,
        ),
      ],
      [4, 3],
      { monospace: true },
    );
    renderer.renderColumns(
      [
        `Imp. Total:`,
        currencyFormat(
          taxVoucher.details.voucherTotals.totalAmount,
          getCurrencyLocaleDataByCountryCode(currencyCode),
          ARCA_CURRENCY_FORMAT_OPTIONS,
        ),
      ],
      [4, 3],
      { monospace: true, bold: true, uppercase: true },
    );
  }
};

const renderOrderFooter: NodeRenderer<{
  taxVoucher: TaxVoucherArca;
}> = async (renderer, { taxVoucher }, { currencyCode }) => {
  const voucherLetter = getVoucherLetter(taxVoucher.details.type);

  if (voucherLetter === 'B') {
    renderer.renderLine('Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)');
    renderer.renderKeyValueLine({
      keyText: 'IVA Contenido',
      valueText: currencyFormat(
        taxVoucher.details.voucherTotals.vatAmount,
        getCurrencyLocaleDataByCountryCode(currencyCode),
        ARCA_CURRENCY_FORMAT_OPTIONS,
      ),
    });
    renderer.renderKeyValueLine({
      keyText: 'Otros impuestos nacionales indirectos',
      valueText: currencyFormat(
        0,
        getCurrencyLocaleDataByCountryCode(currencyCode),
        ARCA_CURRENCY_FORMAT_OPTIONS,
      ),
    });
    renderer.renderEmptyLine();
  }
  renderer.renderKeyValueLine({
    keyText: 'CAE',
    valueText: taxVoucher.details.cae,
  });
  renderer.renderKeyValueLine({
    keyText: 'Vto.CAE',
    valueText: taxVoucher.details.caeFchVto,
  });
  renderer.renderEmptyLine();

  const arcaQrCode = getArcaVoucherQrUrl(taxVoucher);

  const dataUrl = await generateQrDataUrl(arcaQrCode);

  await (<OutputRendererEscPos>renderer).renderImage(dataUrl, {
    algorithm: 'threshold',
    alignment: 'center',
  });
};

async function generateQrDataUrl(text) {
  return new Promise<string>((resolve, reject) => {
    QRCode.toDataURL(
      text,
      {
        margin: 4,
        scale: 4,
        errorCorrectionLevel: 'M',
      },
      function (err, url) {
        if (err) reject(err);
        else resolve(url);
      },
    );
  });
}
